import axios from 'axios';

export interface WhatsAppContact {
  id: string;
  name?: string;
  phone: string;
  lastMessageTime?: string;
  messageCount?: number;
}

export interface WhatsAppInstance {
  id: string;
  phoneNumber?: string;
  isConnected: boolean;
  qrCode?: string;
  qr?: string;  // Some API versions use qr instead of qrCode
  pairingCode?: string;
}

export interface WhatsAppSessionStatus {
  status: 'pending' | 'connected' | 'disconnected' | 'error';
  session_id: string;
  phone_number?: string;
  error_message?: string;
}

export interface SendMessageRequest {
  jid: string;
  message: string | object;
}

class WhatsAppApiClient {
  private baseUrl: string;
  private apiKey: string;

  constructor() {
    // For browser environment, use import.meta.env
    this.baseUrl = import.meta.env.VITE_WHATSAPP_API_URL || 'http://localhost:3000/api';
    this.apiKey = import.meta.env.VITE_WHATSAPP_API_KEY || '';
  }

  /**
   * Create a new WhatsApp instance
   * Note: Do not pass instanceId parameter - let the API generate a new ID
   */
  async createInstance(): Promise<{ data: WhatsAppInstance }> {
    try {
      console.log('Making POST request to create a new instance');
      const response = await axios.post(
        `${this.baseUrl}/whatsapp/instances`,
        {}, // Empty body to let the API generate a new instance ID
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      console.log('Create instance response:', response.data);
      return response.data;
    } catch (error: any) {
      console.error('Error creating WhatsApp instance:', error.response?.data || error.message);
      throw new Error('Failed to create instance');
    }
  }

  /**
   * Get instance details including current QR code and connection status
   */
  async getInstance(instanceId: string): Promise<{ data: WhatsAppInstance }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/whatsapp/instances/${instanceId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error getting instance:', error);
      throw new Error('Failed to get instance');
    }
  }
  
  /**
   * Get the QR code for an instance
   */
  async getQRCode(instanceId: string): Promise<{ qrCode: string }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/whatsapp/instances/${instanceId}/qr`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );

      // Log the response structure to debug
      console.log('QR code response:', JSON.stringify(response.data));
      
      // Handle different response formats
      if (typeof response.data === 'string') {
        return { qrCode: response.data };
      } else if (response.data && typeof response.data.qrCode === 'string') {
        return { qrCode: response.data.qrCode };
      } else if (response.data && typeof response.data.data === 'string') {
        // Handle nested data structure
        return { qrCode: response.data.data };
      } else if (response.data && typeof response.data.code === 'string') {
        // Some API versions return a 'code' property
        return { qrCode: response.data.code };
      } else if (response.data && response.data.success && response.data.data && response.data.data.qrCode) {
        // Handle nested data.qrCode structure with success flag - this matches the exact error format
        console.log('Found nested QR code data with success flag');
        return { qrCode: response.data.data.qrCode };
      } else {
        // Handle the QR code not available error case
        if (response.data && response.data.success === false && 
            response.data.error && typeof response.data.error === 'string' && 
            response.data.error.includes('QR code not available')) {
          console.log('QR code not available - need to delete and recreate instance');
          throw new Error('RECREATE_INSTANCE');
        }
        
        // Handle specific case when the response is binary data
        if (response.data && response.data.base64Image) {
          return { qrCode: response.data.base64Image };
        }
        
        // Check if response.data is itself an object with a QR code property
        const keys = Object.keys(response.data || {});
        if (keys.length > 0) {
          for (const key of ['qr', 'qrcode', 'qr_code', 'code', 'base64']) {
            if (response.data[key] && typeof response.data[key] === 'string') {
              return { qrCode: response.data[key] };
            }
          }
          
          // Check for nested data object
          if (response.data.data && typeof response.data.data === 'object') {
            const dataKeys = Object.keys(response.data.data);
            for (const key of ['qrCode', 'qr', 'qrcode', 'qr_code', 'code', 'base64']) {
              if (response.data.data[key] && typeof response.data.data[key] === 'string') {
                return { qrCode: response.data.data[key] };
              }
            }
          }
          
          // As a fallback, get the raw data from the first key if it's a string
          if (typeof response.data[keys[0]] === 'string') {
            return { qrCode: response.data[keys[0]] };
          }
        }
        
        console.log('Attempting to parse response data for QR code:', JSON.stringify(response.data));
        
        // Try to parse the exact format shown in the error
        try {
          // From the error we can see: {"success":true,"data":{"qrCode":"data:image/png;base64,..."}}
          if (response.data && response.data.success === true && 
              response.data.data && response.data.data.qrCode) {
            return { qrCode: response.data.data.qrCode };
          }
        } catch (e) {
          console.error('Error parsing nested QR code:', e);
        }
        
        console.error('Unexpected QR code response format:', JSON.stringify(response.data));
        throw new Error('QR code in unexpected format');
      }
    } catch (error) {
      console.error('Error getting QR code:', error);
      throw new Error('Failed to get QR code');
    }
  }
  
  /**
   * Get the current status of an instance
   */
  async getStatus(instanceId: string): Promise<{ 
    status: 'connecting' | 'connected' | 'disconnected' | 'failed' | 'not_found',
    phoneNumber?: string,
    id?: string,
    timestamp?: string,
    rawResponse?: any,
    statusCode?: number
  }> {
    try {
      const response = await axios.get(
        `${this.baseUrl}/whatsapp/instances/${instanceId}/status`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );

      console.log('Status response:', JSON.stringify(response.data));
      
      // Success response with full data structure
      // { "success": true, "data": { "id": "instance-id", "isConnected": true, "phoneNumber": "919840792922:93" } }
      if (response.data && response.data.success === true && response.data.data) {
        const instanceData = response.data.data;
        return {
          status: instanceData.isConnected ? 'connected' : 'disconnected',
          phoneNumber: instanceData.phoneNumber,
          id: instanceData.id,
          timestamp: instanceData.timestamp,
          rawResponse: response.data
        };
      }
      
      // Normalize different response formats
      if (typeof response.data === 'string') {
        return { status: response.data as any };
      } else if (response.data && typeof response.data.status === 'string') {
        return response.data;
      } else if (response.data && response.data.data && typeof response.data.data.status === 'string') {
        return { status: response.data.data.status };
      } else if (response.data && response.data.isConnected === true) {
        return { 
          status: 'connected',
          phoneNumber: response.data.phoneNumber,
          id: response.data.id,
          timestamp: response.data.timestamp,
          rawResponse: response.data
        };
      } else if (response.data && response.data.isConnected === false) {
        return { 
          status: 'disconnected',
          id: response.data.id,
          rawResponse: response.data
        };
      } else {
        // Default to connecting if we can't determine
        console.warn('Unknown status format:', response.data);
        return { status: 'connecting' };
      }
    } catch (error: any) {
      console.error('Error getting status:', error);
      
      // Special handling for 404 errors
      if (error.response?.status === 404 || (error.message && error.message.includes('404'))) {
        console.log('Got 404 error in status check - instance not found');
        return {
          status: 'not_found',
          statusCode: 404,
          id: instanceId
        };
      }
      
      throw error;
    }
  }

  /**
   * Send a WhatsApp message
   */
  async sendMessage(instanceId: string, jid: string, message: string | object) {
    try {
      const response = await axios.post(
        `${this.baseUrl}/whatsapp/instances/${instanceId}/messages`,
        {
          jid,
          message,
        },
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
            'Content-Type': 'application/json',
          },
        }
      );

      return response.data;
    } catch (error) {
      console.error('Error sending WhatsApp message:', error);
      throw new Error('Failed to send message');
    }
  }
  
  /**
   * Fetch messages for a specific contact from WhatsApp API
   * @param instanceId The WhatsApp instance ID
   * @param phoneNumber The contact's phone number (without JID format)
   * @param limit Number of messages to retrieve (optional)
   */
  async getContactMessages(instanceId: string, phoneNumber: string, limit?: number) {
    try {
      // Prepare phone number - ensure it has no special characters/spaces
      const cleanPhoneNumber = phoneNumber.replace(/[^0-9]/g, '');
      
      // Build URL with optional limit parameter
      let url = `${this.baseUrl}/whatsapp/instances/${instanceId}/contacts/${cleanPhoneNumber}/messages`;
      if (limit) {
        url += `?limit=${limit}`;
      }
      
      console.log('Fetching messages from API URL:', url);
      
      try {
        const response = await axios.get(url, {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        });
        
        // Handle response in new format with success and data fields
        if (response.data?.success && Array.isArray(response.data?.data)) {
          const messages = response.data.data;
          console.log(`Retrieved ${messages.length} messages from WhatsApp API`);
          return {
            success: true,
            messages: messages,
            pagination: response.data.pagination
          };
        } else if (Array.isArray(response.data)) {
          // Handle case where response is just an array
          console.log(`Retrieved ${response.data.length} messages from WhatsApp API`);
          return {
            success: true,
            messages: response.data,
            pagination: null
          };
        } else if (response.data?.messages && Array.isArray(response.data.messages)) {
          // Handle original expected format
          console.log(`Retrieved ${response.data.messages.length} messages from WhatsApp API`);
          return response.data;
        } else {
          console.warn('Unexpected API response format:', response.data);
          return {
            success: true,
            messages: [],
            pagination: null
          };
        }
      } catch (apiError: any) {
        // Check if this is a 404 (no messages/contact not found)
        if (apiError.response?.status === 404) {
          console.log('No messages found for this contact (404 response) - this is normal for new contacts');
          return { success: true, messages: [], pagination: null };
        }
        
        // For other errors, log and rethrow
        console.error('API error fetching WhatsApp messages:', apiError.response?.data || apiError.message);
        throw apiError;
      }
    } catch (error: any) {
      console.error('Error fetching WhatsApp messages from API:', error.response?.data || error.message);
      // Return empty messages array instead of throwing
      return { success: false, messages: [], error: error.message };
    }
  }

  /**
   * Delete an instance
   */
  /**
   * Get all contacts for a WhatsApp instance
   */
  async getContacts(instanceId: string) {
    try {
      console.log(`Fetching contacts for instance: ${instanceId}`);
      const response = await axios.get(
        `${this.baseUrl}/whatsapp/instances/${instanceId}/contacts`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );
      
      console.log(`Retrieved ${response.data?.contacts?.length || 0} contacts from WhatsApp API`);
      return response.data?.contacts || [];
    } catch (error: any) {
      console.error('Error getting WhatsApp contacts:', error.response?.data || error.message);
      // Return empty array instead of throwing
      return [];
    }
  }
  
  async deleteInstance(instanceId: string) {
    try {
      console.log(`Deleting WhatsApp instance: ${instanceId}`);
      const response = await axios.delete(
        `${this.baseUrl}/whatsapp/instances/${instanceId}`,
        {
          headers: {
            'Authorization': `Bearer ${this.apiKey}`,
          },
        }
      );
      console.log('Delete instance response:', response.data);
      return response.data;
    } catch (error) {
      console.error('Error deleting WhatsApp instance:', error);
      throw new Error('Failed to delete instance');
    }
  }

}

export const whatsappApiClient = new WhatsAppApiClient();